import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUp, MapPin, Phone, X } from "lucide-react";
import { AiSparkle } from "@/components/common/AiSparkle";
import { MiniMarkdown } from "@/components/ai/MiniMarkdown";
import { useAuth } from "@/context/AuthContext";
import { useConcierge } from "@/context/ConciergeContext";
import { ConciergeCards } from "./ConciergeCards";
import { useConciergeChat } from "./useConciergeChat";
import { api } from "@/lib/api";

export const STARTERS = [
  "How far is the closest Italian restaurant from Ghent?",
  "Homes under $450K within 15 minutes of NAS Oceana",
  "Nearest elementary school to Town Center Virginia Beach",
  "What's my home worth?",
];

const Bubble = ({ m, i, onNavigate }) => (m.role === "user" ? (
  <div className="ml-10 self-end rounded-2xl rounded-br-sm bg-ink px-4 py-2.5 text-[15px] text-white" data-testid={`concierge-msg-user-${i}`}>{m.text}</div>
) : (
  <div className="mr-4 flex gap-2.5" data-testid={`concierge-msg-assistant-${i}`}>
    <span className="mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-mist"><AiSparkle className="h-4 w-4" /></span>
    <div className="min-w-0 flex-1">
      <div className={`rounded-2xl rounded-tl-sm px-4 py-2.5 text-[15px] leading-relaxed ${m.error ? "bg-red-50 text-red-700" : "bg-neutral-100 text-neutral-800"}`}>
        {m.pending ? (
          <span className="flex items-center gap-2 text-[13px] text-neutral-500" data-testid="concierge-thinking">
            <span className="ai-typing flex gap-1 text-brass"><span /><span /><span /></span> Looking that up…
          </span>
        ) : m.text ? <MiniMarkdown text={m.text} /> : null}
      </div>
      {m.cards?.length > 0 && <ConciergeCards cards={m.cards} onNavigate={onNavigate} />}
    </div>
  </div>
));

/** The corner AI concierge: answers anything about homes, places, distances and the site, and hands off to a real person. */
export const ConciergePanel = () => {
  const { open, setOpen, pending, clearPending } = useConcierge();
  const { requireAccount } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const chat = useConciergeChat((msg, retry) => requireAccount(msg, retry));
  const { messages, busy, load, send, addLocal, sessionId } = chat;
  const scroller = useRef(null);
  const [teamMessages, setTeamMessages] = useState([]);
  const [locationState, setLocationState] = useState("");
  const [needsLocation, setNeedsLocation] = useState(false);
  const listingKey = pathname.startsWith("/listing/") ? decodeURIComponent(pathname.split("/")[2] || "") : null;

  const ask = async (q) => {
    if (busy) return;
    const d = await send(q, { path: pathname, listing_key: listingKey || null });
    const go = (d?.cards ?? []).find((c) => c.type === "link" && c.navigate);
    if (go) {
      navigate(go.to);
      if (window.innerWidth < 640) setOpen(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, load]);
  useEffect(() => {
    if (!open || !pending) return;
    clearPending();
    load().then(() => ask(pending.q));
  }, [open, pending]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  const loadTeamMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/visitor/messages", { params: { session_id: sessionId } });
      setTeamMessages(Array.isArray(data?.messages) ? data.messages : []);
      setNeedsLocation(!data?.location_shared);
    } catch {
      // Activity tracking may not have completed yet; the next check reconnects quietly.
    }
  }, [sessionId]);
  useEffect(() => {
    if (!open) return undefined;
    loadTeamMessages();
    const timer = setInterval(loadTeamMessages, 10000);
    return () => clearInterval(timer);
  }, [open, loadTeamMessages]);
  const shareLocation = () => {
    if (!navigator.geolocation) { setLocationState("Location sharing is unavailable in this browser."); return; }
    setLocationState("Sharing location…");
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        await api.post("/visitor/location", { session_id: sessionId, lat: position.coords.latitude, lng: position.coords.longitude });
        setLocationState("Location shared with Rob’s team.");
        setNeedsLocation(false);
      } catch {
        setLocationState("We couldn’t save that location.");
      }
    }, () => setLocationState("Location was not shared."), { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
  };

  if (!open) return null;
  const submit = (e) => {
    e.preventDefault();
    const q = e.currentTarget.elements.q.value.trim();
    if (!q) return;
    e.currentTarget.reset();
    ask(q);
  };
  const human = () => addLocal({ role: "assistant", text: "Here's how to reach The Rob Resh Team right now:", cards: [{ type: "call" }] });
  const onNavigate = () => window.innerWidth < 640 && setOpen(false);

  return (
    <div role="region" aria-label="AI home concierge" data-concierge-open="true" data-testid="concierge-panel"
      className="fixed inset-0 z-[60] flex flex-col overflow-hidden bg-white animate-rise-in sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[min(680px,calc(100svh-3rem))] sm:w-[410px] sm:rounded-2xl sm:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]">
      <div className="bg-ink px-5 pb-4 pt-4 text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white/10"><AiSparkle className="h-6 w-6" /></span>
            <div>
              <p className="text-[16px] font-medium">AI Home Concierge <span className="text-[12px] font-normal text-brass">by 757 Realty</span></p>
              <p className="text-[12px] text-white/75">Homes, schools, commutes, restaurants, answered</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={shareLocation} data-testid="concierge-share-location" className="inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-[12px] text-white/80 transition-colors hover:bg-white/10 hover:text-white"><MapPin className="h-4 w-4" /> Share location</button>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close concierge" data-testid="concierge-close"
              className="grid h-9 w-9 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
        </div>
        {locationState && <p className="mt-2 text-[12px] text-white/75" data-testid="concierge-location-status">{locationState}</p>}
        {needsLocation && !locationState && <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-white/15 pt-3" data-testid="concierge-location-prompt"><p className="text-[12px] text-white/75">Share your location so Rob’s team can tailor nearby homes and commutes.</p><div className="flex gap-2"><button type="button" onClick={shareLocation} className="border border-brass px-2.5 py-1 text-[12px] text-brass transition-colors hover:bg-brass hover:text-ink" data-testid="concierge-location-prompt-share">Share</button><button type="button" onClick={() => setNeedsLocation(false)} className="px-2 py-1 text-[12px] text-white/70 transition-colors hover:text-white" data-testid="concierge-location-prompt-dismiss">Not now</button></div></div>}
        <button type="button" onClick={human} data-testid="concierge-human"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-brass py-2.5 text-[14px] font-medium text-ink transition-colors hover:bg-white">
          <Phone className="h-4 w-4" /> Talk to a real person now
        </button>
      </div>
      <div ref={scroller} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-5" data-testid="concierge-messages">
        {messages.length === 0 && (
          <div data-testid="concierge-empty">
            <p className="ai-text font-display text-2xl">Ask me anything.</p>
            <p className="mb-4 mt-2 text-[14px] text-neutral-600">I can find homes, measure the distance to any base, school or restaurant, and open the right page for you.</p>
            <div className="grid gap-2">
              {STARTERS.map((q, i) => (
                <button key={q} type="button" onClick={() => ask(q)} data-sfx="chime" data-testid={`concierge-starter-${i}`}
                  className="flex items-center gap-2 rounded-xl border border-neutral-200 px-3.5 py-2.5 text-left text-[14px] text-ink transition-colors hover:border-ink">
                  <AiSparkle className="h-4 w-4 shrink-0" /> {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} m={m} i={i} onNavigate={onNavigate} />)}
        {teamMessages.map((message) => <div key={message.id} className="mr-4 rounded-2xl rounded-tl-sm border-l-2 border-brass bg-amber-50 px-4 py-3 text-[14px] text-neutral-800" data-testid={`concierge-team-message-${message.id}`}><p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-brass-deep">The Rob Resh Team</p>{message.text}</div>)}
      </div>
      <div className="border-t border-neutral-200 px-3 pb-3 pt-3">
        <div className="ai-glow rounded-full">
          <form onSubmit={submit} className="flex items-center gap-2 rounded-full bg-white p-1.5 pl-4" data-testid="concierge-form">
            <input name="q" maxLength={500} autoComplete="off" placeholder={listingKey ? "Ask about this home or anything else…" : "Ask anything about homes or the area…"}
              className="h-10 min-w-0 flex-1 bg-transparent text-[16px] text-ink placeholder:text-neutral-500 focus:outline-none" data-testid="concierge-input" />
            <button type="submit" disabled={busy} aria-label="Send" data-sfx="chime" data-testid="concierge-send"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-white transition-colors hover:bg-neutral-800 disabled:opacity-50">
              <ArrowUp className="h-4 w-4" />
            </button>
          </form>
        </div>
        <p className="mt-2 text-center text-[11.5px] text-neutral-500">AI can make mistakes · distances are straight-line · places © OpenStreetMap</p>
      </div>
    </div>
  );
};
